import { Controller } from "@hotwired/stimulus";

// Cross-origin-safe file download. Native <a download> is ignored when the
// link target is cross-origin, so clicking just opens the image in a new
// tab. We fetch the URL as a blob, wrap it in object-URL, then click a temp
// anchor to force the browser into save mode.
//
// Falls back to opening in a new tab if the fetch fails (CORS, network) so
// the user always has SOME way to get the file.
export default class extends Controller<HTMLElement> {
  static values = {
    url: String,
    filename: String,
  };

  declare readonly urlValue: string;
  declare readonly filenameValue: string;

  async download(event: MouseEvent): Promise<void> {
    event.preventDefault();
    if (!this.urlValue) return;

    try {
      const response = await fetch(this.urlValue, { credentials: "omit" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const extByType: Record<string, string> = {
        "image/svg+xml": "svg", "image/png": "png", "image/jpeg": "jpg",
        "image/webp": "webp", "image/avif": "avif", "image/gif": "gif",
      };
      const urlExt = this.urlValue.split("?")[0].split(".").pop()?.toLowerCase();
      const safeUrlExt = urlExt && /^[a-z0-9]{1,5}$/.test(urlExt) ? urlExt : null;
      const ext = extByType[blob.type] || safeUrlExt || "png";
      const base = (this.filenameValue || "lumenfall").replace(/\.[^.]+$/, "");
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${base}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Revoke after a tick so the browser has consumed the URL.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.warn("[image-download] blob fetch failed, falling back to open-in-tab", err);
      window.open(this.urlValue, "_blank", "noopener");
    }
  }
}
